//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

/** @public */
export type TierPlan = 'common' | 'rare'

/**
 * Plan-level pooled allowances. Storage, LiveKit minutes and AI tokens are
 * organization-wide pools, not per member.
 *
 * @public
 */
export interface TierLimitsGB {
  storageGB: number
  trafficGB: number
  liveKitMinutes: number
  aiTokens: number
  /** null means unlimited members. */
  maxMembers: number | null
}

/** @public */
export interface TierLimitsBytes {
  storageBytes: number
  trafficBytes: number
  liveKitMinutes: number
  aiTokens: number
  maxMembers: number | null
}

/**
 * Metered overage prices applied after the pooled allowance is used.
 * Storage is charged per GB-month, LiveKit per participant-minute and AI
 * tokens per million tokens.
 *
 * @public
 */
export interface TierOveragePrices {
  storagePerGB: number
  liveKitPerMinute: number
  aiTokensPerMillion: number
}

/**
 * Plan-level pooled allowances, expressed in GB / minutes / tokens.
 *
 * @public
 */
export const TIER_LIMITS_GB: Record<TierPlan, TierLimitsGB> = {
  common: { storageGB: 1, trafficGB: 0, liveKitMinutes: 100, aiTokens: 0, maxMembers: 3 },
  // Keep the historical Stripe plan key (`rare`) while presenting this plan as Pro.
  rare: { storageGB: 10, trafficGB: 0, liveKitMinutes: 600, aiTokens: 1_000_000, maxMembers: null }
}

/**
 * Pay-as-you-go overage prices for the Pro plan. Free has no overage billing.
 *
 * @public
 */
export const TIER_OVERAGE_PRICES: Record<TierPlan, TierOveragePrices> = {
  common: { storagePerGB: 0, liveKitPerMinute: 0, aiTokensPerMillion: 0 },
  rare: { storagePerGB: 0.66, liveKitPerMinute: 0.01, aiTokensPerMillion: 2 }
}

/**
 * Limits used when no plan is active or the plan id is unknown.
 *
 * @public
 */
export const DEFAULT_TIER_LIMITS_GB: TierLimitsGB = TIER_LIMITS_GB.common

const GB_IN_BYTES = 1e9

function normalisePlan (plan: string | undefined): TierPlan | undefined {
  if (plan === undefined) return undefined
  const key = plan.toLowerCase()
  if (key === 'pro' || key === 'epic' || key === 'legendary') return 'rare'
  return key in TIER_LIMITS_GB ? key as TierPlan : undefined
}

/**
 * Resolve plan limits in GB, falling back to the default tier when the plan is
 * unknown or undefined.
 *
 * @public
 */
export function getTierLimitsGB (plan: string | undefined): TierLimitsGB {
  const key = normalisePlan(plan)
  return key !== undefined ? TIER_LIMITS_GB[key] : DEFAULT_TIER_LIMITS_GB
}

/** Resolve the configurable limits for a tier, defaulting to the free tier. @public */
export function getTierLimits (plan: string | undefined): TierLimitsGB {
  return getTierLimitsGB(plan)
}

/** Resolve the overage prices for a tier. @public */
export function getTierOveragePrices (plan: string | undefined): TierOveragePrices {
  const key = normalisePlan(plan)
  return key !== undefined ? TIER_OVERAGE_PRICES[key] : TIER_OVERAGE_PRICES.common
}

/** True when the plan is billed for usage above the pooled allowance. @public */
export function tierAllowsOverage (plan: string | undefined): boolean {
  return normalisePlan(plan) === 'rare'
}

/**
 * Same as {@link getTierLimitsGB}, but expressed in bytes — handy for direct
 * comparison with raw `storageBytes` / `livekitTrafficBytes` usage values.
 *
 * @public
 */
export function getTierLimitsBytes (plan: string | undefined): TierLimitsBytes {
  const limits = getTierLimitsGB(plan)
  return {
    storageBytes: limits.storageGB * GB_IN_BYTES,
    trafficBytes: limits.trafficGB * GB_IN_BYTES,
    liveKitMinutes: limits.liveKitMinutes,
    aiTokens: limits.aiTokens,
    maxMembers: limits.maxMembers
  }
}
